import { sickLeave } from "@/lib/bolovanja";
import { useCallback, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { danas } from "@/lib/dan";
import { dohvatiRaspored, type Raspored } from "@/lib/gradilista";
import {
  hasPermission,
  changedMembershipRanges,
  validateMembers,
  workflow,
  type Crew,
  type CrewMember,
} from "@/lib/workflow";
import {
  cardClass,
  controlClass,
  errorText,
  Feedback,
  PeriodLockNotice,
  useResource,
  useWriteWindow,
} from "./shared";

export function CrewPanel({ siteId }: { siteId: string }) {
  const { user } = useAuth();
  const readRoster = hasPermission(user, "raspored", "read");
  const [active, setActive] = useState("active");
  const [editing, setEditing] = useState<Crew | "new" | null>(null);
  const resource = useResource(
    `${siteId}:${readRoster}`,
    useCallback(
      async (signal) => {
        const [crews, roster] = await Promise.all([
          workflow.crews(siteId, signal),
          readRoster
            ? dohvatiRaspored(siteId, undefined, signal)
            : Promise.resolve([] as Raspored[]),
        ]);
        return { crews, roster };
      },
      [siteId, readRoster],
    ),
  );
  return (
    <section className="space-y-4">
      <Feedback error={resource.error} />
      {resource.loading && <p role="status">Učitavanje brigada…</p>}
      <div className="flex flex-wrap gap-3">
        <label className="grow">
          Prikaz brigada
          <select
            className={controlClass}
            value={active}
            onChange={(e) => setActive(e.target.value)}
          >
            <option value="active">Aktivne</option>
            <option value="inactive">Neaktivne</option>
            <option value="all">Sve</option>
          </select>
        </label>
        {hasPermission(user, "brigada", "create") && (
          <Button
            className="min-h-[52px] self-end"
            onClick={() => setEditing("new")}
          >
            Nova brigada
          </Button>
        )}
      </div>
      {editing &&
        resource.data &&
        hasPermission(
          user,
          "brigada",
          editing === "new" ? "create" : "update",
        ) && (
          <CrewEditor
            key={editing === "new" ? "new" : editing.id}
            siteId={siteId}
            crew={editing === "new" ? null : editing}
            roster={resource.data.roster}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              resource.reload();
            }}
          />
        )}
      {resource.data && (
        <div className="grid gap-4 lg:grid-cols-2">
          {resource.data.crews
            .filter(
              (c) => active === "all" || c.aktivna === (active === "active"),
            )
            .map((crew) => (
              <article key={crew.id} className={cardClass}>
                <h2 className="text-xl font-bold">{crew.naziv}</h2>
                <p>{crew.aktivna ? "Aktivna" : "Neaktivna"}</p>
                <ul className="space-y-2">
                  {crew.clanovi.map((member) => (
                    <li key={member.id}>
                      {resource.data!.roster.find(
                        (r) => r.radnik_id === member.radnik_id,
                      )?.radnik ?? `Radnik ${member.radnik_id.slice(0, 8)}`}
                      {member.je_brigadir ? " · brigadir" : ""}
                      <span className="block text-sm">
                        {member.vrijedi_od} – {member.vrijedi_do ?? "bez kraja"}
                      </span>
                    </li>
                  ))}
                </ul>
                {hasPermission(user, "brigada", "update") && (
                  <Button variant="outline" onClick={() => setEditing(crew)}>
                    Uredi brigadu {crew.naziv}
                  </Button>
                )}
              </article>
            ))}
        </div>
      )}
      {resource.data?.crews.filter(
        (c) => active === "all" || c.aktivna === (active === "active"),
      ).length === 0 && <p>Nema brigada za odabrani filtar.</p>}
    </section>
  );
}
function memberPayload(members: CrewMember[]) {
  return members.map(
    ({ id, radnik_id, je_brigadir, vrijedi_od, vrijedi_do }) => ({
      ...(id ? { id } : {}),
      radnik_id,
      je_brigadir,
      vrijedi_od,
      vrijedi_do,
    }),
  );
}
export function CrewEditor({
  siteId,
  crew,
  roster,
  onClose,
  onSaved,
}: {
  siteId: string;
  crew: Crew | null;
  roster: Raspored[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { user } = useAuth();
  const readSickLeave = hasPermission(user, "bolovanje", "read");
  const absences = useResource(`crew-sick-leave:${siteId}:${readSickLeave}`, useCallback(signal => readSickLeave ? sickLeave.list({ gradiliste_id: siteId }, signal) : Promise.resolve([]), [siteId, readSickLeave]));
  const [name, setName] = useState(crew?.naziv ?? "");
  const [active, setActive] = useState(crew?.aktivna ?? true);
  const [members, setMembers] = useState<CrewMember[]>(
    crew?.clanovi.map((m) => ({ ...m })) ?? [
      {
        radnik_id: "",
        je_brigadir: true,
        vrijedi_od: danas(),
        vrijedi_do: null,
      },
    ],
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const all = [...members, ...(crew?.clanovi ?? [])];
  const start = all.reduce(
    (date, m) => (m.vrijedi_od < date ? m.vrijedi_od : date),
    "9999-12-31",
  );
  const end = all.reduce(
    (date, m) =>
      (m.vrijedi_do ?? "9999-12-31") > date
        ? (m.vrijedi_do ?? "9999-12-31")
        : date,
    "0001-01-01",
  );
  const affected = useMemo(
    () => changedMembershipRanges(crew?.clanovi ?? [], members),
    [crew, members],
  );
  const window = useWriteWindow(siteId, start, end, affected);
  const validation = validateMembers(members);
  const workers = [...new Map(roster.map((r) => [r.radnik_id, r])).values()];
  const update = (index: number, patch: Partial<CrewMember>) =>
    setMembers((rows) =>
      rows.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    );
  const save = async () => {
    if (validation || !name.trim() || window.blocked) return;
    setBusy(true);
    setError(null);
    let savedPart = "";
    try {
      if (!crew)
        await workflow.createCrew(siteId, name.trim(), memberPayload(members));
      else {
        if (!crew.aktivna && active) {
          await workflow.updateCrew(crew.id, name.trim(), active);
          savedPart = "Brigada je aktivirana; ekipa još nije spremljena. ";
        }
        if (
          JSON.stringify(memberPayload(crew.clanovi)) !==
          JSON.stringify(memberPayload(members))
        ) {
          await workflow.updateMembers(crew.id, memberPayload(members));
          savedPart = "Ekipa je spremljena; naziv i aktivnost još nisu. ";
        }
        if (
          (crew.aktivna || !active) &&
          (crew.naziv !== name.trim() || crew.aktivna !== active)
        )
          await workflow.updateCrew(crew.id, name.trim(), active);
      }
      onSaved();
    } catch (error) {
      setError(`${savedPart}${errorText(error)}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      className={cardClass}
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <h2 className="text-xl font-bold">
        {crew ? "Uredi brigadu" : "Nova brigada"}
      </h2>
      <PeriodLockNotice state={window.state} />
      <Feedback error={absences.error} />
      {window.closed && (
        <p role="alert">
          Promjena bi zahvatila ekipu zaključenog dana. Spremanje nije dostupno.
        </p>
      )}
      {window.incomplete && (
        <p role="alert">
          Provjera raspona nije potpuna. Spremanje nije dostupno.
        </p>
      )}
      <Feedback error={error ?? window.error} />
      <label className="block">
        Naziv / broj brigade
        <input
          className={controlClass}
          required
          maxLength={160}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </label>
      {crew && (
        <label className="flex min-h-[52px] items-center gap-3">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
          />
          Brigada je aktivna (isključite za deaktivaciju)
        </label>
      )}
      <div className="space-y-4">
        {members.map((member, index) => (
          <fieldset
            key={member.id ?? index}
            className="avm-surface space-y-3 rounded-lg border p-3"
          >
            <legend>Član {index + 1}</legend>
            <label className="block">
              Radnik
              <select
                className={controlClass}
                required
                disabled={!!member.id || (readSickLeave && (absences.loading || !!absences.error))}
                value={member.radnik_id}
                onChange={(e) => update(index, { radnik_id: e.target.value })}
              >
                <option value="">Odaberite radnika s gradilišta</option>
                {!workers.some((r) => r.radnik_id === member.radnik_id) &&
                  member.radnik_id && (
                    <option value={member.radnik_id}>
                      Radnik {member.radnik_id.slice(0, 8)}
                    </option>
                  )}
                {workers.filter(r => r.radnik_id === member.radnik_id || !absences.data?.some(absence => absence.radnik_id === r.radnik_id && !absence.ponisteno_at && absence.datum_od <= (member.vrijedi_do ?? "9999-12-31") && (absence.datum_do ?? "9999-12-31") >= member.vrijedi_od)).map((r) => (
                  <option key={r.radnik_id} value={r.radnik_id}>
                    {r.radnik ?? `Radnik ${r.radnik_id.slice(0, 8)}`}
                  </option>
                ))}
              </select>
            </label>
            <div className="grid gap-3 sm:grid-cols-2">
              <label>
                Od
                <input
                  className={controlClass}
                  type="date"
                  required
                  value={member.vrijedi_od}
                  onChange={(e) =>
                    update(index, { vrijedi_od: e.target.value })
                  }
                />
              </label>
              <label>
                Do (uključivo, opcionalno)
                <input
                  className={controlClass}
                  type="date"
                  min={member.vrijedi_od}
                  value={member.vrijedi_do ?? ""}
                  onChange={(e) =>
                    update(index, { vrijedi_do: e.target.value || null })
                  }
                />
              </label>
            </div>
            <label className="flex min-h-[52px] items-center gap-3">
              <input
                type="checkbox"
                checked={member.je_brigadir}
                onChange={(e) =>
                  update(index, { je_brigadir: e.target.checked })
                }
              />
              Brigadir
            </label>
            {!member.id && (
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  setMembers((rows) => rows.filter((_, i) => i !== index))
                }
              >
                Ukloni nespremljenog člana
              </Button>
            )}
          </fieldset>
        ))}
      </div>
      <p>
        Brigadir je radnik koji vodi brigadu. Zadnji dan rada u ekipi određujete
        datumom; povijest ostaje sačuvana.
      </p>
      <Button
        type="button"
        variant="outline"
        onClick={() =>
          setMembers((rows) => [
            ...rows,
            {
              radnik_id: "",
              je_brigadir: false,
              vrijedi_od: danas(),
              vrijedi_do: null,
            },
          ])
        }
      >
        Dodaj člana
      </Button>
      {validation && <p role="alert">{validation}</p>}
      <div className="flex flex-wrap gap-3">
        <Button
          disabled={busy || !!validation || !name.trim() || window.blocked}
        >
          Spremi brigadu
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={busy}
          onClick={onClose}
        >
          Odustani
        </Button>
      </div>
    </form>
  );
}
